import { MailSettings } from "./MailSettings";
import { ModelSupplySettings } from "./ModelSupplySettings";
import { PlatformConnections } from "./PlatformConnections";
import { PlatformSettings } from "./PlatformSettings";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Checkbox, NumberField, SearchSelect, Select, Switch, Button, Field } from "../components/rumonai";
import { Avatar } from "../shell/Avatar";
import { accountAmount } from "../hub/modelSupply";
import { instanceStatus, wakesOnDemand } from "../hub/api";
import {
  platformPost, platformRequest, publicationsApi, publicationState, publishedDate, workHref, type AdminAccount,
  type AdminAgent, type AdminOverview, type AuditEntry, type Invite, type Publication
} from "./api";
import {
  CommandFeedback, PageHeading, PlatformDisclosure, PlatformEmpty, PlatformError, PlatformLoading, usePlatformCommand, usePlatformRead
} from "./shared";

const pages = [
  ["", "总览"], ["usage", "平台服务"], ["connections", "渠道连接"], ["accounts", "用户与额度"], ["agents", "运行监控"],
  ["publications", "作品与 Square"], ["settings", "运行设置"], ["mail", "邮件服务"], ["audit", "操作记录"],
] as const;

export function AdminPages({ path }: { path: string }) {
  const page = path.replace(/^#\/?/, "").split("?")[0];
  const params = new URLSearchParams(path.split("?")[1] ?? "");
  const tab = params.get("tab");
  const accountId = params.get("account");
  // On a narrow screen the tab row scrolls sideways; the open tab is kept in view.
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    nav.current?.querySelector("[aria-current]")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [page]);
  return <>
    <nav ref={nav} className="platform-admin-nav" aria-label="平台管理导航">
      {pages.map(([key, label]) => (
        <a key={key} href={`/admin/#/${key}`} aria-current={key === page ? "page" : undefined}>
          {label}
        </a>
      ))}
    </nav>
    {page === "accounts" && accountId ? <AccountDetail key={accountId} accountId={accountId} tab={tab} /> : page === "accounts" ? <>
      <nav className="admin-section-nav" aria-label="用户管理分类">
        <a href="/admin/#/accounts" aria-current={tab !== "credits" ? "page" : undefined}>账号与邀请</a>
        <a href="/admin/#/accounts?tab=credits" aria-current={tab === "credits" ? "page" : undefined}>用户额度</a>
      </nav>
      {tab === "credits" ? <ModelSupplySettings view="credits" /> : <AccountsPage />}
    </>
      : page === "agents" ? <AgentsPage key={params.get("status") ?? "all"} initialStatus={params.get("status") ?? "all"} />
      : page === "publications" ? <PublicationsPage />
      : page === "usage" ? <>
        <nav className="admin-section-nav" aria-label="平台服务分类">
          <a href="/admin/#/usage" aria-current={tab !== "calls" ? "page" : undefined}>提供的服务</a>
          <a href="/admin/#/usage?tab=calls" aria-current={tab === "calls" ? "page" : undefined}>调用记录</a>
        </nav>
        <ModelSupplySettings view={tab === "calls" ? "calls" : "services"} />
      </>
      : page === "connections" ? <PlatformConnections />
      : page === "settings" ? <PlatformSettings />
      : page === "mail" ? <MailSettings />
      : page === "audit" ? <AuditPage />
      : <OverviewPage />}
  </>;
}

function OverviewPage() {
  const reading = usePlatformRead<AdminOverview>("/api/admin/overview");
  return <>
    <PageHeading
      title="运营总览"
      actions={
        <Button variant="secondary" disabled={reading.loading} onClick={reading.reload}>
          <Icon name="refresh" />刷新状态
        </Button>
      }
    >
      <p>查看服务供给与需要处理的事项。</p>
    </PageHeading>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取平台状态" /> : null}
    {reading.data ? (
      <>
        {reading.data.readiness ? <section className="admin-readiness" aria-label="服务状态">
          <div className="admin-readiness-summary"><strong>{reading.data.readiness.chat_model ? `默认对话模型 · ${reading.data.readiness.chat_model}` : "平台对话服务尚未就绪"}</strong>
            <span>{reading.data.readiness.active_services} 项服务已启用</span></div>
          {reading.data.readiness.issues.map(issue => <article key={issue.title}>
            <div><h2>{issue.title}</h2><p>{issue.detail}</p></div>
            <a className="btn" href={issue.href}>{issue.action}<Icon name="arrow" size={16} /></a>
          </article>)}
        </section> : null}
        <div className="platform-stat-grid">
          <a href="/admin/#/accounts">
            <span>用户账号</span>
            <strong>{reading.data.accounts.total}</strong>
            <small>{reading.data.accounts.disabled} 个已暂停访问<Icon name="arrow" /></small>
          </a>
          <a href="/admin/#/agents">
            <span>在线 Agent</span>
            <strong>{reading.data.agents.online}<em> / {reading.data.agents.total}</em></strong>
            <small>{reading.data.agents.cloud} 个云端 · {reading.data.agents.local} 个本地<Icon name="arrow" /></small>
          </a>
          <a href="/admin/#/accounts">
            <span>可用邀请</span>
            <strong>{reading.data.invites.unused}</strong>
            <small>邀请新用户加入内测<Icon name="arrow" /></small>
          </a>
        </div>
        <section className="platform-admin-next">
          <div>
            <Icon name="wall" size={29} />
            <h2>管理公开体验</h2>
            <p>检查作品版本、调整 Square 精选，或暂停有问题的公开服务。</p>
            <a className="platform-work-action" href="/admin/#/publications">
              查看公开作品<Icon name="arrow" />
            </a>
          </div>
          <div>
            <Icon name="money" size={29} />
            <h2>平台服务</h2>
            <p>{reading.data.usage.reason}</p>
            <a className="platform-work-action" href="/admin/#/usage">
              管理平台服务<Icon name="arrow" />
            </a>
          </div>
        </section>
      </>
    ) : null}
  </>;
}

/** The address that opens sign-up with the code filled in; the code sits after the `#`, so no server is sent it. */
const inviteAddress = (code: string) => `${location.origin}/#/join/${encodeURIComponent(code)}`;

function InviteLink({ code }: { code: string }) {
  const [copied, setCopied] = useState<"copied" | "failed" | null>(null);
  const link = inviteAddress(code);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied("copied");
    } catch {
      // No clipboard on a plain-HTTP page: the text is selected for the admin to copy.
      setCopied("failed");
    }
  };
  return <div className="platform-invite-link">
    <input
      className="text-input"
      aria-label="邀请链接"
      value={link}
      readOnly
      onFocus={event => event.target.select()}
    />
    <Button variant="secondary" type="button" name="copy-invite-link" onClick={() => { void copy(); }}>复制链接</Button>
    {copied ? <small className="pa-feedback" role="status">{copied === "copied" ? "已复制" : "没能自动复制，请选中后手动复制"}</small> : null}
  </div>;
}

function AccountsPage() {
  const accounts = usePlatformRead<{ accounts: AdminAccount[] }>("/api/admin/accounts");
  const invitations = usePlatformRead<{ invites: Invite[] }>("/api/admin/invites");
  const [query, setQuery] = useState("");
  const [note, setNote] = useState("");
  const [invite, setInvite] = useState("");
  const command = usePlatformCommand();
  const shown = accounts.data?.accounts.filter(row =>
    row.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  ) ?? [];
  const createInvite = (event: FormEvent) => {
    event.preventDefault();
    void command.run(async () => {
      const result = await platformPost<{ invite: Invite; code: string }>("/api/admin/invites", { note: note.trim() });
      setInvite(result.code); setNote(""); invitations.reload();
    });
  };
  return <>
    <PageHeading title="用户与邀请">
      <p>按用户查看 Agent、额度与调用。</p>
    </PageHeading>
    <CommandFeedback error={command.error} notice={command.notice} />
    <section className="platform-invite-section">
      <div>
        <h2>邀请一位新用户</h2>
        <p>邀请码只在创建后显示一次，请复制后发送给受邀者。</p>
      </div>
      <form onSubmit={createInvite}>
        <label>
          <span className="visually-hidden">邀请备注</span>
          <input
            className="text-input"
            aria-label="邀请备注"
            placeholder="备注，例如第一批内测"
            value={note}
            disabled={command.pending}
            maxLength={200}
            onChange={event => setNote(event.target.value)}
          />
        </label>
        <Button variant="primary" type="submit" aria-busy={command.pending || undefined}

          disabled={command.pending || invitations.loading || Boolean(invitations.error)}
        >
          {command.pending ? "正在创建…" : "生成邀请码"}<Icon name="plus" />
        </Button>
      </form>
      {invite ? (
        <div className="platform-invite-result pa-reveal" role="status">
          <span>把这个链接发给受邀者，打开就能注册</span>
          <InviteLink code={invite} />
          <span>或者只发邀请码</span>
          <input
            className="text-input"
            aria-label="新邀请码"
            value={invite}
            readOnly
            onFocus={event => event.target.select()}
          />
          <small>此处关闭后不会再次显示。</small>
        </div>
      ) : null}
      {invitations.error ? <PlatformError error={invitations.error} retry={invitations.reload} /> : null}
      {invitations.data ? (
        <PlatformDisclosure className="platform-invite-history" title={`邀请记录 · ${invitations.data.invites.length}`}>
          <ul>
            {invitations.data.invites.map(row => (
              <li key={row.id}>
                <span>{row.note || "未填写备注"}</span>
                <span>{row.used_by ? "已使用" : "未使用"}</span>
                <time>{publishedDate(row.created_at)}</time>
              </li>
            ))}
          </ul>
        </PlatformDisclosure>
      ) : null}
    </section>
    <div className="platform-explore-tools">
      <h2>账号</h2>
      <Field className="platform-search" label={<Icon name="search" />}>
        <input
          aria-label="搜索账号"
          placeholder="搜索账号名称"
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </Field>
    </div>
    {accounts.error ? <PlatformError error={accounts.error} retry={accounts.reload} /> : null}
    {!accounts.data && accounts.loading ? <PlatformLoading label="正在读取账号" /> : null}
    {accounts.data ? shown.length ? (
      <div className="platform-account-list">
        {shown.map(row => (
          <article key={row.id}>
            <div className="platform-account-summary">
              <Avatar name={row.name} identity={row.id} glyph="person" className="platform-agent-avatar" />
              <div>
                <h3>
                  <a href={`/admin/#/accounts?account=${encodeURIComponent(row.id)}`}>{row.name}</a>
                  {row.role === "admin" ? <span className="platform-small-tag">管理员</span> : null}
                </h3>
                <p>{row.agent_count} 个 Agent · {row.disabled ? "已暂停访问" : "正常使用"}</p>
              </div>
              <a className="btn" href={`/admin/#/accounts?account=${encodeURIComponent(row.id)}`}>查看用户<Icon name="arrow" size={15} /></a>
            </div>
          </article>
        ))}
      </div>
    ) : (
      <PlatformEmpty title="没有找到账号">
        <p>可以换个名称查找，或先创建邀请。</p>
      </PlatformEmpty>
    ) : null}
  </>;
}

function AccountDetail({ accountId, tab }: { accountId: string; tab: string | null }) {
  const reading = usePlatformRead<{ accounts: AdminAccount[] }>("/api/admin/accounts");
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState("");
  const account = reading.data?.accounts.find(row => row.id === accountId);
  const active = tab === "credits" || tab === "calls" ? tab : "agents";
  const href = `/admin/#/accounts?account=${encodeURIComponent(accountId)}`;
  return <article className="admin-account-detail">
    <a className="admin-back" href="/admin/#/accounts"><Icon name="back" size={16} />返回用户</a>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取用户" /> : null}
    {reading.data && !account ? <PlatformEmpty title="账号不存在"><p>请返回用户列表。</p></PlatformEmpty> : null}
    {account ? <>
      <PageHeading title={account.name} actions={<Button variant="secondary" onClick={() => setEditing(value => !value)}>账号设置</Button>}>
        <p>{account.role === "admin" ? "管理员 · " : ""}{account.disabled ? "已暂停访问" : "正常使用"} · {account.agent_count} 个 Agent
          {" · "}{account.email_verified ? "已验证邮箱" : "没有已验证邮箱，忘记密码时需要发重置码"}</p>
      </PageHeading>
      <CommandFeedback error={null} notice={notice} />
      {editing ? <AccountForm account={account} onDone={message => {
        setEditing(false); if (message) setNotice(message); reading.reload();
      }} /> : null}
      <nav className="admin-section-nav" aria-label="用户详情">
        <a href={href} aria-current={active === "agents" ? "page" : undefined}>Agent</a>
        <a href={`${href}&tab=credits`} aria-current={active === "credits" ? "page" : undefined}>额度</a>
        <a href={`${href}&tab=calls`} aria-current={active === "calls" ? "page" : undefined}>调用记录</a>
      </nav>
      {active === "agents" ? <AgentsPage key={accountId} ownerId={accountId} />
        : <ModelSupplySettings key={`${accountId}:${active}`} view={active} accountId={accountId} />}
    </> : null}
  </article>;
}

function AccountForm({ account, onDone }: { account: AdminAccount; onDone: (notice?: string) => void }) {
  const [disabled, setDisabled] = useState(account.disabled);
  const [limit, setLimit] = useState(account.max_agents === null ? "" : String(account.max_agents));
  const [reason, setReason] = useState("");
  // Shown once, here, and gone with the form: the Hub never answers it again.
  const [resetCode, setResetCode] = useState<{ code: string; expires_at: number } | null>(null);
  const command = usePlatformCommand();
  const valid = reason.trim().length > 0 && (limit === "" || /^\d+$/.test(limit));
  const issueResetCode = () => {
    if (!reason.trim()) return;
    void command.run(async () => {
      setResetCode(await platformPost<{ code: string; expires_at: number }>(
        `/api/admin/accounts/${encodeURIComponent(account.id)}/reset-code`,
        { reason: reason.trim() }
      ));
    });
  };
  const naming = account.role !== "admin";
  const changeAdmin = () => {
    if (!reason.trim()) return;
    void command.run(async () => {
      await platformPost(`/api/admin/accounts/${encodeURIComponent(account.id)}/role`,
        { admin: naming, reason: reason.trim() });
      onDone(naming ? `“${account.name}”已设为管理员。` : `“${account.name}”已不是管理员。`);
    });
  };
  const save = (event: FormEvent) => {
    event.preventDefault(); if (!valid) return;
    void command.run(async () => {
      await platformRequest(`/api/admin/accounts/${encodeURIComponent(account.id)}`, {
        method: "PATCH",
        body: JSON.stringify({ disabled, max_agents: limit === "" ? null : Number(limit), reason: reason.trim() })
      });
      onDone(`“${account.name}”的账号设置已保存。`);
    });
  };
  const revoke = () => {
    if (!reason.trim()) return;
    void command.run(async () => {
      const result = await platformPost<{ revoked: number }>(
        `/api/admin/accounts/${encodeURIComponent(account.id)}/revoke-sessions`,
        { reason: reason.trim() }
      );
      command.setNotice(`${result.revoked} 处登录已退出。`);
    });
  };
  return <form className="platform-admin-form" onSubmit={save}>
    <CommandFeedback error={command.error} notice={command.notice} />
    <div className="platform-field-row">
      <Field className="field" label="可拥有的 Agent 数量" hint={<><small>留空不单独限制；0 表示不能再创建。</small></>}><NumberField
          label="可拥有的 Agent 数量"
          min="0"
          step="1"
          placeholder="不单独限制"
          value={limit}
          disabled={command.pending}
          onChange={event => setLimit(event.target.value)}
        /></Field>
      <Switch label="暂停这个账号访问" checked={disabled} disabled={command.pending} onCheckedChange={setDisabled} />
    </div>
    <Field className="field" label="操作原因"><input
        className="text-input"
        name="reason"
        value={reason}
        disabled={command.pending}
        onChange={event => setReason(event.target.value)}
        placeholder="说明这次调整的原因"
        maxLength={500}
        required
      /></Field>
    <div className="platform-actions">
      <Button variant="primary" type="submit" aria-busy={command.pending || undefined}  disabled={!valid || command.pending}>
        {command.pending ? "正在处理…" : "保存调整"}
      </Button>
      <Button variant="secondary" type="button" name="revoke-sessions" disabled={!reason.trim() || command.pending} onClick={revoke}>
        退出全部登录
      </Button>
      <Button variant="secondary" type="button" name="reset-code" disabled={!reason.trim() || command.pending} onClick={issueResetCode}>
        发重置码
      </Button>
      <Button variant="secondary" type="button" name="admin" value={naming ? "grant" : "revoke"}
        disabled={!reason.trim() || command.pending} onClick={changeAdmin}>
        {naming ? "设为管理员" : "取消管理员"}
      </Button>
      <Button variant="ghost" type="button" disabled={command.pending} onClick={() => onDone()}>
        收起
      </Button>
    </div>
    {resetCode ? (
      <div className="platform-invite-result" role="status">
        <span>请把重置码交给“{account.name}”</span>
        <input
          className="text-input"
          aria-label="重置码"
          name="issued-reset-code"
          value={resetCode.code}
          readOnly
          onFocus={event => event.target.select()}
          // Enter here copies nothing and must not save the form around it.
          onKeyDown={event => { if (event.key === "Enter") event.preventDefault(); }}
        />
        <small>{publishedDate(resetCode.expires_at)} 前有效，只能用一次。此处关闭后不会再次显示。</small>
      </div>
    ) : null}
  </form>;
}

function AgentsPage({ ownerId, initialStatus = "all" }: { ownerId?: string; initialStatus?: string }) {
  const reading = usePlatformRead<{ agents: AdminAgent[] }>(`/api/admin/agents${ownerId ? `?owner=${encodeURIComponent(ownerId)}` : ""}`);
  const [query, setQuery] = useState("");
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState(["all", "online", "sleeping", "disconnected", "stopped"].includes(initialStatus) ? initialStatus : "all");
  const [selected, setSelected] = useState<{ agent: AdminAgent; operation: "start" | "stop" } | null>(null);
  const [reason, setReason] = useState("");
  const command = usePlatformCommand();
  const owners = new Map(reading.data?.agents.map(row => [row.owner, row.owner_name]));
  const shown = reading.data?.agents.filter(row =>
    (!ownerId || row.owner === ownerId) && (!owner || row.owner === owner)
    && `${row.name} ${row.owner_name}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
    && (status === "all" || status === "online" && row.online
      || status === "sleeping" && row.runtime?.state === "sleeping" && wakesOnDemand(row)
      || status === "disconnected" && row.should_run && !row.online && row.runtime?.state !== "sleeping"
      || status === "stopped" && row.location === "cloud" && !row.desired_running && !row.online)
  ) ?? [];
  const groups = new Map<string, AdminAgent[]>();
  for (const agent of shown) groups.set(agent.owner, [...(groups.get(agent.owner) ?? []), agent]);
  const refresh = <Button variant="secondary" disabled={reading.loading} onClick={reading.reload}><Icon name="refresh" />刷新状态</Button>;
  const submit = (event: FormEvent) => {
    event.preventDefault(); if (!selected || !reason.trim()) return;
    const { agent, operation } = selected;
    void command.run(async () => {
      await platformPost(`/api/admin/agents/${encodeURIComponent(agent.id)}/${operation}`, { reason: reason.trim() });
      command.setNotice(`已处理“${agent.name}”的${operation === "start" ? "启动" : "停止"}请求。`);
      setSelected(null);
      setReason("");
      reading.reload();
    });
  };
  return <>
    {ownerId ? <div className="platform-explore-tools"><h2>Agent</h2>{refresh}</div>
      : <PageHeading title="运行监控" actions={refresh}><p>按用户查看连接状态与实例起停。</p></PageHeading>}
    <CommandFeedback error={command.error} notice={command.notice} />
    <div className="admin-agent-filters">
    <Field className="platform-search" label={<Icon name="search" />}>
      <input
        aria-label={ownerId ? "搜索 Agent" : "搜索 Agent 或拥有者"}
        placeholder={ownerId ? "搜索 Agent" : "搜索 Agent 或拥有者"}
        value={query}
        onChange={event => setQuery(event.target.value)}
      />
    </Field>
    {!ownerId ? <SearchSelect label="所属用户" value={owner} onValueChange={setOwner}
      items={[{ value: "", label: "所有用户" }, ...[...owners].map(([id, name]) => ({ value: id, label: name }))]} /> : null}
    <Select label="连接状态" value={status} onValueChange={setStatus}
      items={[{ value: "all", label: "全部状态" }, { value: "online", label: "已连接" }, { value: "sleeping", label: "休眠中" }, { value: "disconnected", label: "应运行但未连接" }, { value: "stopped", label: "已停止" }]} />
    </div>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取实例" /> : null}
    {reading.data ? shown.length ? (
      <div className="admin-owner-groups">
      {[...groups].map(([id, agents]) => <section key={id} className="admin-owner-group">
        {!ownerId ? <div className="admin-owner-heading"><a href={`/admin/#/accounts?account=${encodeURIComponent(id)}`}>{agents[0].owner_name}<Icon name="arrow" size={15} /></a><span>{agents.length} 个 Agent</span></div> : null}
        <div className="platform-instance-list">
        {agents.map(agent => (
          <article key={agent.id}>
            <div className="platform-instance-summary">
              <Avatar name={agent.name} identity={agent.id} className="platform-agent-avatar" />
              <div>
                <h3>{agent.name}</h3>
                <p>{agent.location === "cloud" ? "平台托管" : "本地连接"}</p>
              </div>
              <span className={`platform-instance-status${agent.online ? " is-online" : ""}`}>
                <i />
                {agent.runtime ? instanceStatus(agent).label : agent.online ? "已连接" : agent.location !== "cloud" ? agent.paired === false ? "未配对" : "未连接"
                  : agent.should_run ? "等待连接" : "已停止"}
              </span>
              {agent.location === "cloud" ? (
                <div className="platform-actions">
                  <Button variant="secondary"

                    disabled={command.pending || reading.loading || Boolean(reading.error)}
                    onClick={() => {
                      setSelected({ agent, operation: agent.desired_running || agent.online ? "stop" : "start" });
                      setReason("");
                    }}
                  >
                    {agent.desired_running || agent.online ? "停止实例" : "启动实例"}
                  </Button>
                </div>
              ) : (
                <span className="platform-meta">由本地设备管理</span>
              )}
            </div>
            {selected?.agent.id === agent.id ? (
              <form className="platform-admin-form" onSubmit={submit}>
                <h3>{selected.operation === "stop" ? "停止" : "启动"}“{agent.name}”</h3>
                <p>
                  {selected.operation === "stop"
                    ? "实例停止期间，该 Agent 无法处理新的工作。已有数据保留。"
                    : "启动后需要等待实例连接，连接成功后才能继续工作。"}
                </p>
                <Field className="field" label="操作原因"><input
                    className="text-input"
                    value={reason}
                    maxLength={500}
                    required
                    onChange={event => setReason(event.target.value)}
                  /></Field>
                <div className="platform-actions">
                  <Button variant="primary" type="submit" aria-busy={command.pending || undefined}  disabled={command.pending || !reason.trim()}>
                    {command.pending ? "正在处理…" : "确认执行"}
                  </Button>
                  <Button variant="ghost"

                    type="button"
                    disabled={command.pending}
                    onClick={() => setSelected(null)}
                  >
                    取消
                  </Button>
                </div>
              </form>
            ) : null}
          </article>
        ))}
        </div>
      </section>)}
      </div>
    ) : (
      <PlatformEmpty title="没有找到运行实例">
        <p>{query || owner || status !== "all" ? "没有符合筛选条件的 Agent。" : ownerId ? "该用户尚未创建 IP。" : "用户创建或接入 Agent 后，会出现在这里。"}</p>
      </PlatformEmpty>
    ) : null}
  </>;
}

function PublicationsPage() {
  const reading = usePlatformRead<{ publications: Publication[] }>("/api/admin/publications");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Publication | null>(null);
  const [reason, setReason] = useState("");
  const [featured, setFeatured] = useState(false);
  const [available, setAvailable] = useState(true);
  const command = usePlatformCommand();
  const shown = reading.data?.publications.filter(work =>
    `${work.title} ${work.agent_name}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
  ) ?? [];
  const choose = (work: Publication) => {
    setSelected(work);
    setFeatured(work.featured);
    setAvailable(work.available);
    setReason("");
  };
  const submit = (event: FormEvent) => {
    event.preventDefault(); if (!selected || !reason.trim()) return;
    void command.run(async () => {
      try {
        await publicationsApi.moderate(selected, { available, featured, reason: reason.trim() });
        command.setNotice("作品的公开设置已更新。"); setSelected(null);
      } finally { reading.reload(); }
    });
  };
  return <>
    <PageHeading title="作品与 Square">
      <p>管理精选和公开访问。暂停服务后，已有作品链接也会停止提供体验。</p>
    </PageHeading>
    <CommandFeedback error={command.error} notice={command.notice} />
    <Field className="platform-search platform-search-wide" label={<Icon name="search" />}>
      <input
        aria-label="搜索公开作品"
        placeholder="搜索作品或 Agent"
        value={query}
        onChange={event => setQuery(event.target.value)}
      />
    </Field>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取公开作品" /> : null}
    {reading.data ? shown.length ? (
      <div className="platform-publication-list">
        {shown.map(work => (
          <article key={work.id}>
            <div className="platform-publication-info">
              <p className="platform-agent-credit">{work.agent_name}</p>
              <h3>{work.title}</h3>
              <p className="platform-meta">
                {publicationState(work)}
                {work.featured ? <span>Square 精选</span> : null}
                <span>{publishedDate(work.created_at)}</span>
              </p>
              <PlatformDisclosure className="platform-version" title="版本信息">
                <code>{work.version}</code>
              </PlatformDisclosure>
            </div>
            <div className="platform-actions">
              {work.available ? (
                <a className="btn" href={workHref(work.id)}>
                  打开作品<Icon name="arrow" />
                </a>
              ) : null}
              <Button variant="secondary"

                disabled={reading.loading || Boolean(reading.error) || command.pending}
                onClick={() => choose(work)}
              >
                管理公开设置
              </Button>
            </div>
            {selected?.id === work.id ? (
              <form className="platform-admin-form" onSubmit={submit}>
                <div className="platform-field-row">
                  <Checkbox label="加入 Square 精选" checked={featured} disabled={command.pending} onCheckedChange={checked => setFeatured(checked === true)} />
                  <Switch label="允许公开访问" checked={available} disabled={command.pending || work.status === "withdrawn"} onCheckedChange={setAvailable} />
                </div>
                {work.status === "withdrawn" ? <p>拥有者已撤回这个版本，平台不能替它重新发布。</p> : null}
                <Field className="field" label="操作原因"><input
                    className="text-input"
                    value={reason}
                    maxLength={500}
                    required
                    onChange={event => setReason(event.target.value)}
                  /></Field>
                <div className="platform-actions">
                  <Button variant="primary" type="submit" aria-busy={command.pending || undefined}  disabled={command.pending || !reason.trim()}>
                    {command.pending ? "正在保存…" : "保存公开设置"}
                  </Button>
                  <Button variant="ghost"

                    type="button"
                    disabled={command.pending}
                    onClick={() => setSelected(null)}
                  >
                    取消
                  </Button>
                </div>
              </form>
            ) : null}
          </article>
        ))}
      </div>
    ) : (
      <PlatformEmpty title="还没有公开作品">
        <p>拥有者公开作品后，可以在这里管理展示和访问。</p>
      </PlatformEmpty>
    ) : null}
  </>;
}


/** The Hub's record codes, in the page's words; the code stays in the raw record. */
const acts: Record<string, string> = {
  "account.role": "调整角色", "account.update": "修改账号", "account.revoke_sessions": "退出全部登录",
  "account.password.change": "修改密码", "account.reset_code.issue": "发重置码",
  "account.reset_code.use": "用重置码设置新密码", "account.reset_code.expire": "重置码过期",
  "account.reset_link.issue": "发出重置链接", "account.reset_link.use": "用重置链接设置新密码",
  "account.email.set": "填写邮箱", "account.email.confirm": "确认邮箱",
  "mail.settings.update": "修改邮件服务", "mail.test": "发送测试邮件",
  "invite.create": "发出邀请", "agent.rename": "Agent 改名", "agent.start": "启动 Agent", "agent.stop": "停止 Agent",
  "model.service.update": "修改模型服务", "model.service.test": "测试模型连接", "model.allowance.update": "调整额度", "model.allowance.adjust": "发放或收回额度", "model.call.reconcile": "核定调用费用",
  "publication.moderation": "管理作品", "platform.settings.update": "修改平台设置",
  "platform.credentials.update": "修改渠道连接",
};
/** An operation that waits on something outside records its request and its outcome apart. */
const stages: Record<string, string> = { requested: "已发出", completed: "已完成", failed: "未成功" };
/** A role change is named by where it went. */
const roleActs: Record<string, string> = { admin: "设为管理员", owner: "取消管理员" };

/** What one record did: the act, and the facts its details carry. */
function auditWords({ action, details }: AuditEntry): { act: string; facts: string[] } {
  const parts = action.split(".");
  const stage = stages[parts[parts.length - 1]];
  const code = (stage ? parts.slice(0, -1) : parts).join(".");
  const { before, after, error } = details;
  const said = (value: unknown) => typeof value === "string" || typeof value === "number" ? String(value) : "";
  const money = (value: unknown) => typeof value === "number" ? accountAmount(value) : "";
  const when = (value: unknown) => typeof value === "number" ? publishedDate(value) : "";
  const listed = (value: unknown) => Array.isArray(value) ? value.filter(item => typeof item === "string").join("、") : "";
  const act = (code === "account.role" ? roleActs[said(after)] : undefined) ?? acts[code] ?? "平台操作";
  const change = after && typeof after === "object" ? after as Record<string, unknown> : {};
  const facts = code === "account.role" ? []
    : code === "account.password.change" ? [`${said(details.revoked)} 处其他登录已退出`, details.code_voided ? "未用的重置码已作废" : ""]
    : code === "account.reset_code.issue" ? [`${when(details.expires_at)} 前有效`, details.replaced ? "之前的重置码已作废" : ""]
    : code === "account.reset_code.use" || code === "account.reset_link.use" ? [Number(details.revoked) ? `${said(details.revoked)} 处登录已退出` : ""]
    : code === "mail.settings.update" ? [details.password_changed ? "换了密码" : ""]
    : code === "mail.test" ? [details.ok ? "测试成功" : "测试失败"]
    : code === "account.reset_code.expire" ? [`${when(details.expires_at)} 到期`]
    : code === "agent.rename" ? [`${said(before)} → ${said(after)}`]
    : code === "account.update" ? [
      change.disabled === true ? "暂停账号" : change.disabled === false ? "恢复账号" : "",
      "max_agents" in change ? `Agent 上限 ${said(change.max_agents) || "不限"}` : "",
    ]
    : code === "account.revoke_sessions" ? [`${said(details.revoked)} 处登录已退出`]
    : code === "model.service.update" ? [details.enabled ? "已启用" : "已停用", details.key_changed ? "换了密钥" : ""]
    : code === "model.allowance.update" ? [`额度 ${money(details.allowance_cny)}`]
    : code === "model.allowance.adjust" ? [`调整 ${money(details.amount_cny)} · 累计发放 ${money(details.after_cny)}`]
    : code === "model.service.test" ? [details.status === "passed" ? "测试成功" : details.status === "failed" ? "测试失败" : "结果未确认"]
    : code === "model.call.reconcile" ? [`核定 ${money(details.charged_cny)}`]
    : code === "publication.moderation" ? [
      details.available === false ? "下架" : details.available === true ? "恢复" : "",
      details.featured === true ? "推荐" : details.featured === false ? "取消推荐" : "",
    ]
    // The application and the fields the save moved; their values are never in the record.
    : code === "platform.credentials.update" ? [
      said(details.title),
      listed(details.changed) ? `登记 ${listed(details.changed)}` : "",
      listed(details.cleared) ? `清除 ${listed(details.cleared)}` : "",
    ]
    // Each setting the save moved, by the name the Hub recorded, from what to what.
    : code === "platform.settings.update" ? (Array.isArray(details.changes) ? details.changes : []).map(item => {
      const { name, before, after, unit } = (item ?? {}) as Record<string, unknown>;
      return `${said(name)} ${said(before)} → ${said(after)}${said(unit) ? ` ${said(unit)}` : ""}`;
    })
    : [];
  return { act: stage ? `${act} · ${stage}` : act, facts: [...facts, said(error)].filter(Boolean) };
}

function AuditPage() {
  const reading = usePlatformRead<{ audit: AuditEntry[] }>("/api/admin/audit?limit=100");
  return <>
    <PageHeading
      title="操作记录"
      actions={
        <Button variant="secondary" disabled={reading.loading} onClick={reading.reload}>
          <Icon name="refresh" />刷新记录
        </Button>
      }
    >
      <p>最近 100 次平台操作，保留操作者、对象和原因。</p>
    </PageHeading>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取操作记录" /> : null}
    {reading.data ? reading.data.audit.length ? (
      <ol className="platform-audit-list">
        {reading.data.audit.map(entry => {
          const { act, facts } = auditWords(entry);
          return <li key={entry.id}>
            <div className="platform-audit-top">
              <strong>{entry.actor_name || entry.actor_id}</strong>
              <span>{entry.target_name ? `${act} · ${entry.target_name}` : act}</span>
              <time>{new Date(entry.created_at * 1000).toLocaleString("zh-CN")}</time>
            </div>
            {facts.length ? <p>{facts.join("，")}</p> : null}
            {entry.reason ? <p>{entry.reason}</p> : null}
            <PlatformDisclosure title="原始记录">
              <pre>{JSON.stringify({ action: entry.action, target: entry.target_id, details: entry.details }, null, 2)}</pre>
            </PlatformDisclosure>
          </li>;
        })}
      </ol>
    ) : (
      <PlatformEmpty title="还没有管理操作">
        <p>邀请、账号调整和实例管理等操作会记录在这里。</p>
      </PlatformEmpty>
    ) : null}
  </>;
}
